/**
 * Service de statistiques financières
 * Calcule les totaux, moyennes et autres statistiques
 */

import { getFilteredTransactions } from './TransactionService';
import { supabase } from '../supabaseClient';
import { DEFAULT_COLORS } from '../../config/constants';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';
import { countsAsOperatingIncomeOrExpense } from '../transactionCashflow';

/**
 * Interface pour les statistiques de base
 */
export interface BasicStats {
  totalDepenses: number;
  totalRevenus: number;
  solde: number;
  nombreTransactions: number;
}

/**
 * Interface pour les statistiques par période
 */
export interface PeriodStats extends BasicStats {
  periode: string;
  moyenneDepenses: number;
  moyenneRevenus: number;
}

/**
 * Interface pour les statistiques par catégorie
 */
export interface CategoryStats {
  categorie_id: string;
  categorie_nom: string;
  categorie_couleur: string;
  categorie_icone?: string | null;
  total: number;
  nombreTransactions: number;
  pourcentage: number;
}

/**
 * Interface pour les statistiques par compte
 */
export interface AccountStats {
  compte_id: string;
  compte_nom: string;
  compte_couleur: string;
  total: number;
  nombreTransactions: number;
  pourcentage: number;
}

/**
 * Calcule les statistiques de base pour une période donnée
 */
export const calculateBasicStats = async (
  startDate?: string,
  endDate?: string,
  excludeTransfers = false
): Promise<{ data: BasicStats | null; error: any }> => {
  try {
    const filters: any = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;

    const { data: transactions, error } = await getFilteredTransactions(filters);
    if (error) {
      return { data: null, error };
    }

    if (!transactions || transactions.length === 0) {
      return {
        data: {
          totalDepenses: 0,
          totalRevenus: 0,
          solde: 0,
          nombreTransactions: 0
        },
        error: null
      };
    }

    // Filtrer transferts / prêts / remboursements (hors budget)
    const transactionsFiltered = transactions.filter((transaction) =>
      countsAsOperatingIncomeOrExpense(
        transaction.type,
        transaction.categorie?.nom,
      ),
    );

    // Si excludeTransfers demandé explicitement, le filtre ci-dessus couvre déjà transfert
    void excludeTransfers;

    const stats = transactionsFiltered.reduce(
      (acc, transaction) => {
        const montant = Math.abs(transaction.montant);
        
        if (transaction.type === 'depense' || (!transaction.type && transaction.montant < 0)) {
          acc.totalDepenses += montant;
        } else if (transaction.type === 'revenu' || (!transaction.type && transaction.montant > 0)) {
          acc.totalRevenus += montant;
        }
        
        acc.nombreTransactions++;
        return acc;
      },
      {
        totalDepenses: 0,
        totalRevenus: 0,
        nombreTransactions: 0
      }
    );

    const solde = stats.totalRevenus - stats.totalDepenses;

    return {
      data: {
        ...stats,
        solde
      },
      error: null
    };
  } catch (error) {
    console.error('Exception lors du calcul des statistiques de base:', error);
    return { data: null, error };
  }
};

/**
 * Calcule les statistiques par catégorie
 */
export const calculateCategoryStats = async (
  startDate?: string,
  endDate?: string,
  excludeTransfers = false
): Promise<{ data: CategoryStats[] | null; error: any }> => {
  try {
    const filters: any = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;

    const { data: transactions, error } = await getFilteredTransactions(filters);
    if (error) {
      return { data: null, error };
    }

    if (!transactions || transactions.length === 0) {
      return { data: [], error: null };
    }

    // Filtrer transferts / prêts / remboursements
    const transactionsFiltered = transactions.filter((transaction) =>
      countsAsOperatingIncomeOrExpense(
        transaction.type,
        transaction.categorie?.nom,
      ),
    );
    void excludeTransfers;

    // Calculer le total général des dépenses pour les pourcentages
    const totalGeneral = transactionsFiltered
      .filter(transaction => transaction.type === 'depense' || transaction.montant < 0)
      .reduce((sum, transaction) => sum + Math.abs(transaction.montant), 0);

    // Grouper par catégorie
    const categoryMap = new Map<string, CategoryStats>();

    transactionsFiltered.forEach(transaction => {
      const categorieId = transaction.categorie_id;
      
      // Pour les dépenses par catégorie, on ne prend que les dépenses (montants négatifs)
      // et on les convertit en positif pour l'affichage
      if (transaction.montant < 0) {
        const montant = Math.abs(transaction.montant);

        if (!categoryMap.has(categorieId)) {
          categoryMap.set(categorieId, {
            categorie_id: categorieId,
            categorie_nom: transaction.categorie?.nom || 'Catégorie inconnue',
            categorie_couleur: transaction.categorie?.couleur || DEFAULT_COLORS.UNKNOWN,
            categorie_icone: transaction.categorie?.icone ?? 'tag',
            total: 0,
            nombreTransactions: 0,
            pourcentage: 0
          });
        }

        const categoryStat = categoryMap.get(categorieId)!;
        categoryStat.total += montant;
        categoryStat.nombreTransactions++;
      }
    });

    // Calculer les pourcentages et trier par total décroissant
    const categoryStats = Array.from(categoryMap.values())
      .map(stat => ({
        ...stat,
        pourcentage: totalGeneral > 0 ? (stat.total / totalGeneral) * 100 : 0
      }))
      .sort((a, b) => b.total - a.total);

    return { data: categoryStats, error: null };
  } catch (error) {
    console.error('Exception lors du calcul des statistiques par catégorie:', error);
    return { data: null, error };
  }
};

/**
 * Calcule les statistiques par compte
 */
export const calculateAccountStats = async (
  startDate?: string,
  endDate?: string,
  excludeTransfers = false
): Promise<{ data: AccountStats[] | null; error: any }> => {
  try {
    const filters: any = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;

    const { data: transactions, error } = await getFilteredTransactions(filters);
    if (error) {
      return { data: null, error };
    }

    if (!transactions || transactions.length === 0) {
      return { data: [], error: null };
    }

    // Filtrer les transferts si demandé
    const transactionsFiltered = excludeTransfers
      ? transactions.filter(transaction => {
          // Exclure les transactions de type "transfert"
          if (transaction.type === 'transfert') {
            return false;
          }
          // Exclure les transactions dont la catégorie contient "transfert" dans son nom
          const categorieNom = transaction.categorie?.nom?.toLowerCase() || '';
          if (categorieNom.includes('transfert')) {
            return false;
          }
          return true;
        })
      : transactions;

    // Calculer le total général pour les pourcentages
    const totalGeneral = transactionsFiltered.reduce((sum, transaction) => sum + Math.abs(transaction.montant), 0);

    // Grouper par compte
    const accountMap = new Map<string, AccountStats>();

    transactionsFiltered.forEach(transaction => {
      const compteId = transaction.compte_id;
      const montant = Math.abs(transaction.montant);

      if (!accountMap.has(compteId)) {
        accountMap.set(compteId, {
          compte_id: compteId,
          compte_nom: transaction.compte?.nom || 'Compte inconnu',
          compte_couleur: transaction.compte?.couleur || DEFAULT_COLORS.UNKNOWN,
          total: 0,
          nombreTransactions: 0,
          pourcentage: 0
        });
      }

      const accountStat = accountMap.get(compteId)!;
      accountStat.total += montant;
      accountStat.nombreTransactions++;
    });

    // Calculer les pourcentages et trier par total décroissant
    const accountStats = Array.from(accountMap.values())
      .map(stat => ({
        ...stat,
        pourcentage: totalGeneral > 0 ? (stat.total / totalGeneral) * 100 : 0
      }))
      .sort((a, b) => b.total - a.total);

    return { data: accountStats, error: null };
  } catch (error) {
    console.error('Exception lors du calcul des statistiques par compte:', error);
    return { data: null, error };
  }
};

/**
 * Calcule les statistiques mensuelles pour une année donnée
 */
export const calculateMonthlyStats = async (year: number): Promise<{ data: PeriodStats[] | null; error: any }> => {
  try {
    const monthlyStats: PeriodStats[] = [];

    for (let month = 0; month < 12; month++) {
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const { data: basicStats, error } = await calculateBasicStats(startDate, endDate, false);
      if (error) {
        console.error(`Erreur lors du calcul des stats pour ${year}-${month + 1}:`, error);
        continue;
      }

      if (basicStats) {
        const monthName = new Date(year, month, 1).toLocaleDateString('fr-FR', { month: 'long' });
        
        monthlyStats.push({
          ...basicStats,
          periode: monthName,
          moyenneDepenses: basicStats.nombreTransactions > 0 ? basicStats.totalDepenses / basicStats.nombreTransactions : 0,
          moyenneRevenus: basicStats.nombreTransactions > 0 ? basicStats.totalRevenus / basicStats.nombreTransactions : 0
        });
      }
    }

    return { data: monthlyStats, error: null };
  } catch (error) {
    console.error('Exception lors du calcul des statistiques mensuelles:', error);
    return { data: null, error };
  }
}; 

/**
 * Calcule le solde total réel basé sur le solde initial des comptes + toutes les transactions
 * Plus précis que le champ 'solde' des comptes qui peut être obsolète
 */
export const calculateRealTotalBalance = async (): Promise<{ data: number | null; error: any }> => {
  try {
    console.log('🔍 calculateRealTotalBalance - Début du calcul');
    
    // Vérifier que l'utilisateur est connecté
    const { data: { session }, error: authError } = await supabase.auth.getSession();
    if (authError || !session?.user) {
      console.error('❌ Utilisateur non connecté');
      return { data: null, error: 'Utilisateur non connecté' };
    }
    
    // Vérifier s'il y a une impersonation active
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || session.user.id;
    
    console.log(`👤 Utilisateur connecté: ${session.user.email}`);
    if (impersonatedUserId) {
      console.log(`🎭 Mode impersonation actif pour l'utilisateur: ${impersonatedUserId}`);
    }
    
    // Récupérer les transactions en utilisant le service qui gère l'impersonation
    // ou directement avec l'ID approprié
    let transactions: any[] | null = null;
    let transactionsError: any = null;
    
    if (impersonatedUserId) {
      // Utiliser RPC admin pour contourner RLS et avoir les jointures côté JSON
      const rpc = await supabase.rpc('admin_get_transactions', { p_user_id: userId });
      if (rpc.error) {
        console.error('❌ Erreur RPC admin_get_transactions:', rpc.error);
        transactionsError = rpc.error;
      } else {
        transactions = (rpc.data as any[]) || [];
      }
    } else {
      // Utiliser la requête normale
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId);
      transactions = data;
      transactionsError = error;
    }
    
    if (transactionsError) {
      console.error('❌ Erreur lors de la récupération directe des transactions:', transactionsError);
      return { data: null, error: transactionsError };
    }

    console.log(`📊 Transactions récupérées directement: ${transactions?.length || 0}`, transactions);

    // Récupérer les comptes en utilisant le service qui gère l'impersonation
    let comptes: any[] | null = null;
    let comptesError: any = null;
    
    if (impersonatedUserId) {
      // Utiliser RPC admin pour contourner RLS
      const rpc = await supabase.rpc('admin_get_comptes', { p_user_id: userId });
      if (rpc.error) {
        console.error('❌ Erreur RPC admin_get_comptes:', rpc.error);
        comptesError = rpc.error;
      } else {
        comptes = (rpc.data as any[]) || [];
      }
    } else {
      // Utiliser la requête normale
      const { data, error } = await supabase
        .from('comptes')
        .select('*')
        .eq('user_id', userId);
      comptes = data;
      comptesError = error;
    }

    if (comptesError) {
      console.error('❌ Erreur lors de la récupération des comptes:', comptesError);
      return { data: null, error: comptesError };
    }

    console.log(`🏦 Comptes récupérés directement: ${comptes?.length || 0}`, comptes);

    // Calculer le solde total des comptes
    const soldeComptes = comptes?.reduce((sum, compte) => {
      const soldeCompte = compte.solde || 0;
      console.log(`💰 Compte ${compte.nom}: solde=${compte.solde}, utilisé=${soldeCompte}`);
      return sum + soldeCompte;
    }, 0) || 0;

    console.log(`💵 Solde total des comptes: ${soldeComptes}`);

    // Calculer la somme des transactions
    let sommeTransactions = 0;
    if (transactions && transactions.length > 0) {
      sommeTransactions = transactions.reduce((sum, transaction) => {
        return sum + transaction.montant;
      }, 0);
    }

    console.log(`💸 Somme des transactions: ${sommeTransactions}`);

    // Pour l'instant, retourner seulement le solde des comptes
    // const totalBalance = soldeComptes + sommeTransactions;
    const totalBalance = soldeComptes; // Test: seulement le solde des comptes
    
    console.log(`🎯 Solde total final: ${totalBalance} (${soldeComptes} + ${sommeTransactions})`);
    console.log(`🔧 DEBUG: Retourne seulement le solde des comptes: ${totalBalance}`);

    return { data: totalBalance, error: null };
  } catch (error) {
    console.error('❌ Exception lors du calcul du solde total réel:', error);
    return { data: null, error };
  }
};

/**
 * Calcule les statistiques de comparaison mensuelle
 */
export const calculateMonthlyComparison = async (
  currentMonth: string, // Format: 'YYYY-MM'
  previousMonth: string // Format: 'YYYY-MM'
): Promise<{ data: any | null; error: any }> => {
  try {
    // Extraire l'année et le mois avec validation
    const currentParts = currentMonth.split('-');
    const previousParts = previousMonth.split('-');
    
    if (currentParts.length !== 2 || previousParts.length !== 2) {
      return { data: null, error: 'Format de date invalide' };
    }
    
    const currentYear = parseInt(currentParts[0]!, 10);
    const currentMonthNum = parseInt(currentParts[1]!, 10);
    const previousYear = parseInt(previousParts[0]!, 10);
    const previousMonthNum = parseInt(previousParts[1]!, 10);
    
    // Valider que les valeurs sont des nombres valides
    if (isNaN(currentYear) || isNaN(currentMonthNum) || isNaN(previousYear) || isNaN(previousMonthNum)) {
      return { data: null, error: 'Format de date invalide' };
    }
    
    // Valider que les mois sont dans la plage valide (1-12)
    if (currentMonthNum < 1 || currentMonthNum > 12 || previousMonthNum < 1 || previousMonthNum > 12) {
      return { data: null, error: 'Mois invalide' };
    }
    
    // Calculer les dates de début et fin pour chaque mois
    const currentStart = `${currentYear}-${String(currentMonthNum).padStart(2, '0')}-01`;
    const currentEnd = `${currentYear}-${String(currentMonthNum).padStart(2, '0')}-${new Date(currentYear, currentMonthNum, 0).getDate()}`;
    const previousStart = `${previousYear}-${String(previousMonthNum).padStart(2, '0')}-01`;
    const previousEnd = `${previousYear}-${String(previousMonthNum).padStart(2, '0')}-${new Date(previousYear, previousMonthNum, 0).getDate()}`;
    
    // Valider que les dates sont valides
    if (!currentStart || !currentEnd || !previousStart || !previousEnd) {
      return { data: null, error: 'Erreur lors de la génération des dates' };
    }
    
    // Récupérer les statistiques pour les deux mois
    const [currentStats, previousStats] = await Promise.all([
      calculateBasicStats(currentStart, currentEnd, false),
      calculateBasicStats(previousStart, previousEnd, false)
    ]);
    
    if (currentStats.error || previousStats.error) {
      return { data: null, error: currentStats.error || previousStats.error };
    }
    
    if (!currentStats.data || !previousStats.data) {
      return { data: null, error: 'Données manquantes pour la comparaison' };
    }
    
    // Calculer les variations
    const comparison = {
      current: currentStats.data,
      previous: previousStats.data,
      changes: {
        income: previousStats.data.totalRevenus > 0 
          ? ((currentStats.data.totalRevenus - previousStats.data.totalRevenus) / previousStats.data.totalRevenus) * 100 
          : 0,
        expenses: previousStats.data.totalDepenses > 0 
          ? ((currentStats.data.totalDepenses - previousStats.data.totalDepenses) / previousStats.data.totalDepenses) * 100 
          : 0,
        balance: previousStats.data.solde !== 0 
          ? ((currentStats.data.solde - previousStats.data.solde) / Math.abs(previousStats.data.solde)) * 100 
          : 0,
        savingsRate: (() => {
          const currentRate = currentStats.data.totalRevenus > 0 
            ? ((currentStats.data.totalRevenus - currentStats.data.totalDepenses) / currentStats.data.totalRevenus) * 100 
            : 0;
          const previousRate = previousStats.data.totalRevenus > 0 
            ? ((previousStats.data.totalRevenus - previousStats.data.totalDepenses) / previousStats.data.totalRevenus) * 100 
            : 0;
          return currentRate - previousRate;
        })()
      }
    };
    
    return { data: comparison, error: null };
  } catch (error) {
    console.error('Exception lors du calcul de la comparaison mensuelle:', error);
    return { data: null, error };
  }
}; 